'use client'

/**
 * Shared PWA install state — ONE module-level capture of `beforeinstallprompt` so the
 * auto-banner, homepage "Get the App" section, admin CTA, and EA reminder all coordinate
 * (multiple components each adding their own listener would race / double-preventDefault).
 */
import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null
let appInstalled = false
let initialized = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function init() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred = e as BeforeInstallPromptEvent
    emit()
  })
  window.addEventListener('appinstalled', () => {
    appInstalled = true
    deferred = null
    emit()
  })
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua)
  const iPadOS = navigator.platform === 'MacIntel' && (navigator as unknown as { maxTouchPoints: number }).maxTouchPoints > 1
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome/.test(ua)
  return (iOS || iPadOS) && isSafari
}

/** Desktop macOS Safari — installs via File → Add to Dock (no beforeinstallprompt). */
export function isMacSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isMac = navigator.platform === 'MacIntel' || /Macintosh/.test(ua)
  const noTouch = (navigator as unknown as { maxTouchPoints: number }).maxTouchPoints <= 1
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|Edg/.test(ua)
  return isMac && noTouch && isSafari
}

export function isFirefox(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Firefox|FxiOS/.test(navigator.userAgent)
}

/** Platform-appropriate manual-install steps when no native prompt is available. */
export function manualInstallHint(): string {
  if (isIos()) return 'Tap the Share button, then "Add to Home Screen".'
  if (isMacSafari()) return 'In Safari: File menu → "Add to Dock".'
  if (isFirefox()) return 'Open this site in Chrome or Edge to install the app.'
  return 'Open your browser menu (⋮) → "Install Spire" / "Add to Home screen".'
}

/** Trigger the native install prompt (Android / desktop Chromium). Returns the outcome or null. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (!deferred) return null
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  deferred = null
  emit()
  return outcome
}

export function useInstall() {
  const [, setTick] = useState(0)
  useEffect(() => {
    init()
    const cb = () => setTick((t) => t + 1)
    listeners.add(cb)
    cb() // sync once on mount (event may have fired before this component mounted)
    return () => { listeners.delete(cb) }
  }, [])

  return {
    canInstall: !!deferred,                 // native prompt available (Android/desktop Chromium)
    installed: appInstalled || isStandalone(),
    isStandalone: isStandalone(),
    isIos: isIos(),
    isMacSafari: isMacSafari(),
    isFirefox: isFirefox(),
    manualHint: manualInstallHint(),
    promptInstall,
  }
}
